import { motion, useReducedMotion } from "motion/react";
import type { CSSProperties } from "react";
import type { ClockState } from "@/hooks/useClock";

const NUMERALS: { label: string; rot: number }[] = [
  { label: "XII", rot: 0 },
  { label: "III", rot: 90 },
  { label: "VI", rot: 180 },
  { label: "IX", rot: 270 },
];

const TICKS = [30, 60, 120, 150, 210, 240, 300, 330];

const numeralStyle = (rot: number): CSSProperties => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  fontFamily: "var(--tc-serif)",
  fontSize: 14,
  color: "var(--tc-text)",
  transform: `translate(-50%,-50%) rotate(${rot}deg) translateY(-70px) rotate(${-rot}deg)`,
});

const tickStyle = (rot: number): CSSProperties => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  width: 2,
  height: 7,
  background: "var(--tc-muted)",
  transform: `translate(-50%,-50%) rotate(${rot}deg) translateY(-72px)`,
});

const handBase: CSSProperties = {
  position: "absolute",
  left: "50%",
  bottom: "50%",
  transformOrigin: "bottom center",
  borderRadius: 3,
};

/** Vintage analog face. Second hand "ticks" with a stiff spring. */
export function AnalogClock({ clock }: { clock: ClockState }) {
  const reduced = useReducedMotion();
  const handTransition = (stiffness: number) =>
    reduced ? { duration: 0 } : { type: "spring" as const, stiffness, damping: 26, mass: 0.6 };

  return (
    <div style={{ margin: "22px 0 0", position: "relative", width: 184, height: 184 }}>
      <motion.div
        style={{ position: "absolute", inset: -14, borderRadius: "50%", border: "1px dashed var(--tc-brass-line)" }}
        animate={reduced ? undefined : { rotate: 360 }}
        transition={reduced ? undefined : { duration: 60, ease: "linear", repeat: Infinity }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: "radial-gradient(circle at 50% 38%, var(--tc-bg-1) 0%, var(--tc-paper) 78%, var(--tc-paper-2) 100%)",
          boxShadow:
            "inset 0 0 0 2px var(--tc-brass-line), inset 0 0 0 6px var(--tc-paper), inset 0 0 0 7px var(--tc-line), 0 16px 34px -18px rgba(0,0,0,.45)",
        }}
      >
        {NUMERALS.map((n) => (
          <span key={n.label} style={numeralStyle(n.rot)}>{n.label}</span>
        ))}
        {TICKS.map((rot) => (
          <span key={rot} style={tickStyle(rot)} />
        ))}

        {/* hour */}
        <motion.div
          style={{ ...handBase, width: 5, height: 46, background: "var(--tc-ink)", x: "-50%" }}
          animate={{ rotate: clock.hourAngle }}
          transition={handTransition(120)}
        />
        {/* minute */}
        <motion.div
          style={{ ...handBase, width: 3, height: 64, background: "var(--tc-muted)", x: "-50%" }}
          animate={{ rotate: clock.minAngle }}
          transition={handTransition(160)}
        />
        {/* second */}
        <motion.div
          style={{ ...handBase, width: 2, height: 70, background: "var(--tc-brass)", x: "-50%" }}
          animate={{ rotate: clock.secAngle }}
          transition={handTransition(700)}
        />

        {/* hub */}
        <motion.div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 11,
            height: 11,
            marginTop: -5.5,
            marginLeft: -5.5,
            borderRadius: "50%",
            background: "var(--tc-brass)",
            boxShadow: "0 0 0 2px var(--tc-paper)",
          }}
          animate={reduced ? undefined : { scale: [1, 1.35, 1], opacity: [1, 0.5, 1] }}
          transition={reduced ? undefined : { duration: 2, ease: "easeInOut", repeat: Infinity }}
        />
      </div>
    </div>
  );
}
